import React, { useRef, useMemo } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Divider, IconButton, Menu, MenuItem, Slider, Switch,
} from '@mui/material';
import { DeleteOutlined, EditOutlined, FormatSizeOutlined, LabelOutlined, LineWeightOutlined, MoreVertOutlined } from '@mui/icons-material';
import FieldComponent from '../../components/@generalComponents/FieldComponent';
import GeneralText from '../general-components/GeneralText';
import Avatar from '../general-components/Avatar';
import { useTranslation } from 'react-i18next';


const ItemTypes = {
  ELEMENT: 'element',
};

const DraggableElementComponent = ({
  element,
  index,
  moveElement,
  updateField,
  handleFieldChange,
  handleSave,
  currentMetric,
  handleFieldChangeMetric,
  layout,
  blockWith,
  fontColor,
  hoveredIndex,
  setHoveredIndex,
  handleOpenMenu,
  anchorEl,
  menuOpen,
  handleCloseMenu,
  currentMenuItem,
  handleLabelChange,
  handleTypeChange,
  handleSizeChange,
  handleWeightChange,
  handleDeleteItem,
  menuItemIndex
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: ItemTypes.ELEMENT,
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveElement(dragIndex, hoverIndex);

      item.index = hoverIndex;
    },
  });


  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ELEMENT,
    item: () => ({ index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const memoizedStyle = useMemo(() => ({
    opacity: isDragging ? 0.3 : 1,
    cursor: 'move',
    transition: 'opacity 0.2s ease-in-out, background-color 0.2s ease-in-out',
  }), [isDragging]);


  const memoizedContent = useMemo(() => {
    return (
      <div className={element?.fieldType === 'field' ? "col-12" : "col-11"}>
        {element?.fieldType === 'field' ? (
          <>
            {element?.structureValue === 'metric' ? (
              <FieldComponent
                key={'metric' + element?.structureValue}
                field={{
                  typeData: element?.typeValue, label: element?.label, name: element?.label,
                  selections: element?.selections, value: element?.structureValue,
                }}
                defaultValue={element?.structureValue} value={currentMetric ?? 0}
                onChange={handleFieldChangeMetric} handleSave={handleSave}
              />
            ) : (
              <FieldComponent
                key={element?.structureValue}
                field={{
                  typeData: element?.typeValue, label: element?.label, name: element?.label,
                  selections: element?.selections, value: element?.structureValue,
                }}
                defaultValue={element?.structureValue} value={element?.value ?? ''}
                onChange={(field, value) => handleFieldChange(field, value, index)}
                handleSave={handleSave}
              />
            )}
          </>
        ) : (
          <>
            {(element?.typeValue === 'avatar' || element?.typeValue === 'media' ||
              element?.typeValue === 'media-single' || element?.typeValue === 'media-single-private') && (
                <Avatar
                  key={`avatar-${element?.structureValue}`}
                  img={element?.value ?? ''} name={element?.name ?? ''} alt={element?.value ?? ''}
                  type={element?.typeValue} label={element?.label} blockWith={blockWith}
                  size={'large'}
                />
              )}
            {element?.typeValue !== 'custom:statuses' && element?.typeValue !== 'avatar' &&
              element?.typeValue !== 'media' && element?.typeValue !== 'media-single' &&
              element?.typeValue !== 'media-single-private' && (
                <GeneralText
                  key={`text-${element?.structureValue}`}
                  primary={element?.valueColor === 'primary'} layout={layout}
                  size={element?.weight || 'regular'}
                  fontSize={
                    layout?.data?.length > 1
                      ? element?.size + 'px' || '12px'
                      : (element?.transformedValue || element?.value)?.length < 10
                        ? element?.typeValue === 'list' ? (element?.size + 'px' || '12px') : '18px'
                        : element?.size + 'px' || '12px'
                  }
                  label={layout?.header?.labels ? element?.showLabel && element?.label : '' || ''}
                  structureId={element?.structureId}
                  color={layout?.data?.length > 1 ? element?.valueColor : fontColor || element?.valueColor}
                  text={element?.transformedValue || element?.value || '-'}
                  type={element?.typeValue}
                  classNameComponent={element?.action === 'none' ? '' : 'hover'}
                  selections={element?.selections || []}
                  keyStructure={element?.structureValue}
                  action={element?.action}
                  onClick={() => updateField(
                    element?.structureValue, element?.value, element?.label,
                    element?.typeValue, element?.selections, element?.action,
                    [{
                      collectionField: element?.collectionField, structureId: element?.structureId,
                      primaryData: element?.primaryData,
                    }]
                  )}
                />
              )}
          </>
        )}
      </div>
    );
  }, [
    element,
    index,
    currentMetric,
    handleFieldChangeMetric,
    handleSave,
    handleFieldChange,
    updateField,
    blockWith,
    layout,
    fontColor
  ]);

  // --- Memoize Menu Button and Menu Section ---
  const memoizedMenuSection = useMemo(() => {
    const showButton = hoveredIndex === index && !isDragging;
    const showMenu = showButton && currentMenuItem && Boolean(anchorEl) && menuItemIndex === index && menuOpen;

    return (
      <>
        {showButton && (
          <div className="col-1">
            <IconButton
              sx={{ marginTop: '-5px' }}
              onClick={(event) => handleOpenMenu(event, index)}
              aria-label="options"
            >
              <MoreVertOutlined sx={{ fontSize: '14px', color: 'grey' }} />
            </IconButton>

            {showMenu && Boolean(anchorEl) && (
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                PaperProps={{
                  sx: {
                    borderRadius: '12px',
                    overflow: 'visible',
                    padding: '5px',
                    marginTop: '5px'
                  }
                }}
              >
                <div className="px-3 mt-2">
                  <GeneralText text={currentMenuItem?.label || t('editItem')} fontSize="12px" primary={true} size="bold" classNameComponent="mb-2" />
                  <Divider component={'div'} />
                </div>
                <MenuItem disableRipple divider sx={{ p: 0.8, m: 0 }}>
                  <div className="d-flex middle-content" style={{ width: 150, padding: '2px' }}>
                    <EditOutlined sx={{ mr: 1.2 }} />
                    <Switch
                      sx={{ marginTop: '-2px' }} size="small"
                      checked={!!currentMenuItem?.fieldType}
                      onChange={(e) => handleTypeChange(e, menuItemIndex)}
                      aria-labelledby={`label-switch-label-${index}`}
                    />
                    <GeneralText id={`label-switch-type-${index}`} text={currentMenuItem?.fieldType === 'field' ? t('editable') : t('static')} fontSize="11px" color="grey" classNameComponent="px-2" />
                  </div>
                </MenuItem>
                <MenuItem disableRipple divider sx={{ p: 0.8, m: 0 }}>
                  <div className="d-flex middle-content" style={{ width: 150, padding: '2px' }}>
                    <LabelOutlined sx={{ mr: 1.2 }} />
                    <Switch
                      sx={{ marginTop: '-2px' }} size="small"
                      checked={!!currentMenuItem?.showLabel}
                      onChange={(e) => handleLabelChange(e, menuItemIndex)}
                      aria-labelledby={`label-switch-label-${index}`}
                    />
                    <GeneralText id={`label-switch-label-${index}`} text={currentMenuItem?.showLabel ? t('showLabel') : t('hideLabel')} fontSize="11px" color="grey" classNameComponent="px-2" />
                  </div>
                </MenuItem>
                <MenuItem disableRipple divider sx={{ p: 0.8, m: 0 }}>
                  <div className="d-flex" style={{ width: 150, padding: '2px' }}>
                    <FormatSizeOutlined sx={{ mr: 1.2, mt: 0.5 }} />
                    <Slider
                      value={currentMenuItem?.size || 12}
                      onChange={(event, newValue) => handleSizeChange(event, newValue, menuItemIndex)}
                      step={1} min={8} max={18}
                      marks={[{ value: 8 }, { value: 10 }, { value: 12 }, { value: 14 }, { value: 16 }, { value: 18 }]}
                      size="small"
                      valueLabelDisplay="auto"
                      aria-label={t('fontSize')}
                    />
                  </div>
                </MenuItem>
                <MenuItem disableRipple divider sx={{ p: 0.8, m: 0 }}>
                  <div className="d-flex" style={{ width: 150, padding: '2px' }}>
                    <LineWeightOutlined sx={{ mr: 1.2, mt: 0.5 }} />
                    <Slider
                      value={currentMenuItem?.weight === 'regular' ? 0 : currentMenuItem?.weight === 'medium' ? 1 : 2}
                      onChange={(event, newValue) => handleWeightChange(event, newValue, menuItemIndex)} // Assumed stable
                      step={1} min={0} max={2}
                      marks={[{ value: 0, label: 'R' }, { value: 1, label: 'M' }, { value: 2, label: 'B' }]}
                      size="small"
                      aria-label={t('fontWeight')}
                    />
                  </div>
                </MenuItem>
                <MenuItem sx={{ p: 0.8, m: 0 }} onClick={() => handleDeleteItem(menuItemIndex)}> {/* Assumed stable */}
                  <div className="d-flex middle-content" style={{ width: 150, padding: '2px', color: 'red' }}>
                    <DeleteOutlined sx={{ mr: 1.2 }} />
                    <GeneralText text={t('delete')} fontSize="12px" classNameComponent="mt-1" primary={true} />
                  </div>
                </MenuItem>
              </Menu>
            )}
          </div>
        )}
      </>
    );
  }, [
    index,
    hoveredIndex,
    isDragging,
    menuItemIndex,
    menuOpen,
    currentMenuItem,
    handleOpenMenu,
    handleCloseMenu,
    handleLabelChange,
    handleSizeChange,
    handleWeightChange,
    handleDeleteItem,
    t
  ]);

  return (
    <div
      ref={ref}
      style={memoizedStyle}
      onMouseEnter={() => {
        if (hoveredIndex !== index) setHoveredIndex(index);
      }}
      onMouseLeave={() => {
        setHoveredIndex(null);
      }}
      className="mt-3 d-flex"
      data-testid={`draggable-element-${index}`}
    >
      {memoizedContent}
      {memoizedMenuSection}
    </div>
  );
};

const DraggableElement = React.memo(DraggableElementComponent);

export default DraggableElement;